import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import styles from './FileModal.module.css';
import ModalLayout from '../general/modal/ModalLayout';
import { DocxPreview } from '@nordinsight/implant-core/ui';
import { getDocumentPreviewType } from '../../utils/files';

export interface FileModalFile {
    url: string;
    title?: string;
    filename?: string;
    description?: string;
    documentType: string;
    mimeType?: string;
}

interface FileModalProps {
    file: FileModalFile;
    close: () => void;
}

const FileModal: React.FC<FileModalProps> = ({ file, close }) => {
    const { t } = useTranslation();
    const {
        url,
        title,
        filename,
        description,
        documentType,
        mimeType,
    } = file;
    const documentPreviewType = getDocumentPreviewType({
        filename: filename ?? title,
        mimeType,
    });
    return createPortal(
        <ModalLayout
            hasCloseButton={true}
            size={documentType === 'document' ? 'xl' : 'large'}
            onClose={close}
            important
        >
            <div className={styles.wrapper}>
                <h2>{title}</h2>
                <p>{description}</p>
                <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="mb-2 self-start text-xs text-muted-foreground underline hover:text-foreground"
                >
                    {t('videoOverview.fileModal.openInNewTab')}
                </a>
                <div className={styles.content}>
                    {documentType === 'video' && (
                        <video className={styles.fileVideo} controls autoPlay>
                            <source src={url} type="video/mp4" />
                            {t(
                                'videoOverview.fileModal.video.unsupportedMessage'
                            )}
                        </video>
                    )}
                    {documentType === 'image' && (
                        <img
                            className={styles.fileImage}
                            src={url}
                            alt={title}
                        />
                    )}
                    {documentType === 'document' &&
                        documentPreviewType === 'docx' && (
                        <DocxPreview
                            url={url}
                            title={title ?? 'Document'}
                            className="h-full min-h-0 w-full self-stretch"
                        />
                    )}
                    {documentType === 'document' &&
                        documentPreviewType === 'pdf' && (
                        <embed
                            className={styles.filePdf}
                            src={url}
                            type="application/pdf"
                            width="100%"
                            height="100%"
                        />
                    )}
                    {((documentType === 'document' &&
                        documentPreviewType === 'unsupported') ||
                        documentType === 'other') && (
                            <div
                                role="status"
                                className="flex min-h-40 items-center justify-center p-6 text-center text-sm text-muted-foreground"
                            >
                                {t(
                                    'videoOverview.fileModal.document.unsupportedMessage'
                                )}
                            </div>
                        )}
                </div>
            </div>
        </ModalLayout>,
        document.body
    );
};
export default FileModal;
